import { HelpPlacement } from "./registry";
import {
  Box,
  Button,
  Paper,
  Popper,
  Stack,
  Typography,
  Unstable_TrapFocus as TrapFocus,
  useTheme,
} from "@mui/material";
import { PopperProps } from "@mui/material/Popper";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

type PopperInstance = NonNullable<
  Extract<PopperProps["popperRef"], React.RefObject<unknown>>["current"]
>;

const SPOTLIGHT_PADDING = 6;
const ARROW_SIZE = 12;

const OPPOSITE: Record<HelpPlacement, HelpPlacement> = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
};

/** The opposite side first, then the other two. */
const fallbacks = (placement: HelpPlacement): HelpPlacement[] => [
  OPPOSITE[placement],
  ...(["bottom", "top", "right", "left"] as HelpPlacement[]).filter(
    (side) => side !== placement && side !== OPPOSITE[placement]
  ),
];

interface HelpCalloutProps {
  /** The control pointed at; null shows the callout in the middle of the page. */
  anchor: HTMLElement | null;
  title?: string;
  body: string;
  placement?: HelpPlacement;
  /** Tours dim the page around the anchor and hold focus; hints do neither. */
  modal: boolean;
  step?: { index: number; total: number };
  primaryLabel: string;
  onPrimary: () => void;
  /** Shows Skip (tours). Esc calls it, or onPrimary when there is none. */
  onSkip?: () => void;
  /** A click on the anchored control itself. */
  onAnchorClick?: () => void;
  /** Hints close on any press anywhere on the page. */
  closeOnAnyPress?: boolean;
  /** The anchor left the page or was hidden. */
  onAnchorLost: () => void;
}

/** The anchor's box, kept current through scroll, resize and layout changes. */
const useAnchorRect = (anchor: HTMLElement | null): DOMRect | null => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    if (!anchor) {
      setRect(null);
      return undefined;
    }
    const update = () => setRect(anchor.getBoundingClientRect());
    update();
    window.addEventListener("resize", update);
    // Capture, so scrolling any container (not only the window) is seen.
    window.addEventListener("scroll", update, true);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(anchor);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      observer?.disconnect();
    };
  }, [anchor]);
  return rect;
};

const isHidden = (anchor: HTMLElement) => {
  if (!anchor.isConnected) {
    return true;
  }
  const { width, height } = anchor.getBoundingClientRect();
  return width === 0 && height === 0;
};

/**
 * A rounded-rectangle hole in a full-page path, for `clip-path`. Pointer
 * events follow the clip, so the anchored control stays clickable through
 * the hole while the rest of the dim layer swallows clicks.
 */
const spotlightPath = (rect: DOMRect, radius: number) => {
  const x = rect.left - SPOTLIGHT_PADDING;
  const y = rect.top - SPOTLIGHT_PADDING;
  const w = rect.width + SPOTLIGHT_PADDING * 2;
  const h = rect.height + SPOTLIGHT_PADDING * 2;
  const r = Math.min(radius + SPOTLIGHT_PADDING, w / 2, h / 2);
  const W = window.innerWidth;
  const H = window.innerHeight;
  return (
    `path(evenodd, "M0 0H${W}V${H}H0Z ` +
    `M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}` +
    `V${y + h - r}A${r} ${r} 0 0 1 ${x + w - r} ${y + h}` +
    `H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}` +
    `V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z")`
  );
};

/**
 * A small card that points at one control: an arrow on the side facing it,
 * a title, a sentence or two, a step count, and its buttons. Positioned with
 * MUI's Popper, which flips the placement when there is no room.
 */
const HelpCallout = ({
  anchor,
  title,
  body,
  placement = "bottom",
  modal,
  step,
  primaryLabel,
  onPrimary,
  onSkip,
  onAnchorClick,
  closeOnAnyPress = false,
  onAnchorLost,
}: HelpCalloutProps) => {
  const theme = useTheme();
  const rect = useAnchorRect(anchor);
  const popperRef = useRef<PopperInstance | null>(null);
  const [arrow, setArrow] = useState<HTMLElement | null>(null);

  // Before showing, bring the control into view.
  useEffect(() => {
    anchor?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [anchor]);

  // Popper follows scroll and resize itself; layout changes need a nudge.
  useEffect(() => {
    popperRef.current?.update();
  }, [rect]);

  useEffect(() => {
    if (!anchor) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      if (isHidden(anchor)) {
        onAnchorLost();
      }
    }, 500);
    return () => window.clearInterval(timer);
  }, [anchor, onAnchorLost]);

  useEffect(() => {
    if (!anchor || !onAnchorClick) {
      return undefined;
    }
    anchor.addEventListener("click", onAnchorClick);
    return () => anchor.removeEventListener("click", onAnchorClick);
  }, [anchor, onAnchorClick]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        (onSkip ?? onPrimary)();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onPrimary, onSkip]);

  useEffect(() => {
    if (!closeOnAnyPress) {
      return undefined;
    }
    document.addEventListener("pointerdown", onPrimary, true);
    return () => document.removeEventListener("pointerdown", onPrimary, true);
  }, [closeOnAnyPress, onPrimary]);

  const radius = anchor
    ? parseFloat(getComputedStyle(anchor).borderRadius) || 0
    : 0;

  const card = (
    <Paper
      role="dialog"
      aria-label={title ?? "Tip"}
      tabIndex={-1}
      elevation={8}
      sx={{
        p: 4,
        maxWidth: 320,
        border: 1,
        borderColor: "divider",
        outline: "none",
        position: "relative",
      }}
    >
      {title && (
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {title}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        {body}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mt: 3 }}
        justifyContent="flex-end"
      >
        {step && step.total > 1 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: "auto" }}
          >
            {step.index + 1} of {step.total}
          </Typography>
        )}
        {onSkip && (
          <Button size="small" onClick={onSkip}>
            Skip
          </Button>
        )}
        <Button size="small" variant="contained" onClick={onPrimary}>
          {primaryLabel}
        </Button>
      </Stack>
    </Paper>
  );

  const content = modal ? <TrapFocus open>{card}</TrapFocus> : card;
  const zIndex = theme.zIndex.modal + 2;

  return (
    <>
      {modal && (
        <Box
          data-testid="help-spotlight"
          aria-hidden
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.preventDefault()}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: zIndex - 1,
            bgcolor: "rgba(0, 0, 0, 0.55)",
            clipPath: rect ? spotlightPath(rect, radius) : undefined,
          }}
        />
      )}
      {anchor ? (
        <Popper
          open
          anchorEl={anchor}
          placement={placement}
          popperRef={popperRef}
          modifiers={[
            { name: "offset", options: { offset: [0, SPOTLIGHT_PADDING + 8] } },
            { name: "preventOverflow", options: { padding: 8 } },
            // Popper only tries the opposite side by default. A side with no
            // room on either side (a sidebar link on a phone) would then
            // leave the card over the control it points at.
            {
              name: "flip",
              options: { padding: 8, fallbackPlacements: fallbacks(placement) },
            },
            { name: "arrow", enabled: true, options: { element: arrow } },
          ]}
          sx={{
            zIndex,
            "& .help-arrow": {
              position: "absolute",
              width: ARROW_SIZE,
              height: ARROW_SIZE,
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                transform: "rotate(45deg)",
              },
            },
            '&[data-popper-placement^="bottom"] .help-arrow': {
              top: -ARROW_SIZE / 2,
            },
            '&[data-popper-placement^="top"] .help-arrow': {
              bottom: -ARROW_SIZE / 2,
            },
            '&[data-popper-placement^="left"] .help-arrow': {
              right: -ARROW_SIZE / 2,
            },
            '&[data-popper-placement^="right"] .help-arrow': {
              left: -ARROW_SIZE / 2,
            },
          }}
        >
          <Box sx={{ position: "relative" }}>
            {/* Behind the card, which covers the inner half of the square. */}
            <Box ref={setArrow} className="help-arrow" />
            {content}
          </Box>
        </Popper>
      ) : (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex,
          }}
        >
          {content}
        </Box>
      )}
    </>
  );
};

export default HelpCallout;
